import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import AuthContext from '@/context/AuthProvider';
import {
  PANEL_VERIFICATION_REQUIRED_EVENT,
  type PanelVerificationRequiredDetail,
} from '@utils/panelVerification';
import type {
  LegalVerificationStatus,
  PanelVerificationSession,
} from '@/types/auth';
import { PanelVerificationModal } from './PanelVerificationModal';
import { PanelVerificationNotice } from './PanelVerificationNotice';
import { isLegalVerificationStatus } from './legalVerificationStatus';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Una sola apertura automática por sesión del navegador y por panel. */
const AUTO_OPEN_KEY = 'panel-verification-auto-opened';
/** El tooltip del chip se muestra solo la primera vez que se cierra el modal. */
const COACHMARK_KEY = 'panel-verification-coachmark-seen';
const COACHMARK_DURATION_MS = 8000;
/** Cada cuánto se consulta `get-profile` mientras el webhook activa el panel. */
export const ACTIVATION_POLL_MS = 10000;
/** Tras este tiempo sin activarse se deja de consultar y se remite a soporte. */
export const ACTIVATION_POLL_TIMEOUT_MS = 10 * 60 * 1000;

const SUPER_ADMIN_PERMISSION = 'all:all';

// Preferencias de la vista, no estado del negocio: si el almacenamiento no está
// disponible (navegación privada, bloqueado), el flujo funciona igual.
const readFlag = (storage: Storage, key: string): boolean => {
  try {
    return storage.getItem(key) === '1';
  } catch {
    return false;
  }
};
const writeFlag = (storage: Storage, key: string): void => {
  try {
    storage.setItem(key, '1');
  } catch {
    // Sin almacenamiento: el modal o el tooltip pueden volver a aparecer.
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────

export interface PanelVerificationContextValue {
  /**
   * Panel `pending`, con o sin `allow_operations`: muestra el chip y se puede
   * verificar desde él.
   */
  isPending: boolean;
  /**
   * Panel `pending` sin `allow_operations`: el backend rechaza sus acciones
   * (1185). Los migrados con uso (CORP-596) quedan `pending` con el flag y
   * operan normal (CORP-601).
   */
  areOperationsBlocked: boolean;
  /** Owner o super admin: puede completar la verificación. */
  canVerify: boolean;
  isCoachmarkVisible: boolean;
  /**
   * Estado del trámite en Legal para la UI: el de `get-profile` o, mientras el
   * SDK está abierto, el último que reportó. `null` sin trámite.
   */
  legalStatus: LegalVerificationStatus | null;
  /** La espera de la activación pasó su tope y se dejó de consultar. */
  isActivationDelayed: boolean;
  /** Abre el modal (quien puede verificar) o el aviso (el resto). */
  openVerification: () => void;
  /**
   * Corre `action` solo si el panel puede operar; si no, abre la verificación.
   * Para flujos que el backend no puede frenar a tiempo, como una recarga por
   * transferencia: el dinero se mueve fuera del panel antes de reportarlo.
   */
  requireVerified: (action: () => void) => void;
}

const PanelVerificationContext = createContext<PanelVerificationContextValue>({
  isPending: false,
  areOperationsBlocked: false,
  canVerify: false,
  isCoachmarkVisible: false,
  legalStatus: null,
  isActivationDelayed: false,
  openVerification: () => undefined,
  requireVerified: (action) => action(),
});

export const usePanelVerification = (): PanelVerificationContextValue =>
  useContext(PanelVerificationContext);

// ─── Provider ─────────────────────────────────────────────────────────────────

export interface PanelVerificationProviderProps {
  children: React.ReactNode;
}

/**
 * Verificación del panel (CORP-599). Un panel pendiente navega normal; sin
 * `allow_operations` sus acciones las rechaza el backend con 1185 y `ApiRest`
 * lo avisa por evento (con el flag opera normal y solo se verifica desde el
 * chip, CORP-601).
 * Aquí se decide qué ver: el modal con el SDK de Legal para owner o super
 * admin, el aviso de contactar al administrador para el resto. El modal se
 * abre solo al entrar y se puede cerrar sin perder avance (el trámite vive en
 * Legal); se retoma desde el chip del header.
 */
export const PanelVerificationProvider: React.FC<
  PanelVerificationProviderProps
> = ({ children }) => {
  const { datalogin, refreshProfile } = useContext(AuthContext) as {
    datalogin: PanelVerificationSession | null;
    refreshProfile: (options?: {
      throwServerErrors?: boolean;
    }) => Promise<void>;
  };

  // «Pendiente» y «bloqueado» van separados (CORP-601): con `allow_operations`
  // el panel sigue pendiente —chip y verificación por clic— pero opera normal,
  // sin auto-apertura ni `requireVerified`.
  const isPending = datalogin?.verification_status === 'pending';
  const areOperationsBlocked =
    isPending && datalogin?.allow_operations !== true;
  const canVerify =
    datalogin?.is_owner === true ||
    (datalogin?.permissions ?? []).includes(SUPER_ADMIN_PERMISSION);
  const corporateId = datalogin?.corporate_id ?? '';
  const isActive = datalogin?.verification_status === 'active';
  const profileLegalStatus = isLegalVerificationStatus(
    datalogin?.legal_verification_status,
  )
    ? datalogin.legal_verification_status
    : null;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);
  const [isCoachmarkVisible, setIsCoachmarkVisible] = useState(false);
  // Pista del SDK abierto (`onStatusChange`). Solo para la UI, nunca se guarda
  // ni sustituye al webhook. Vale mientras el perfil siga diciendo lo mismo que
  // cuando llegó: en cuanto `get-profile` trae otro estado, gana el perfil.
  const [liveLegalStatus, setLiveLegalStatus] = useState<{
    corporateId: string;
    profileStatus: LegalVerificationStatus | null;
    status: LegalVerificationStatus;
  } | null>(null);
  const legalStatus =
    liveLegalStatus?.corporateId === corporateId &&
    liveLegalStatus.profileStatus === profileLegalStatus
      ? liveLegalStatus.status
      : profileLegalStatus;

  // `get-profile` para la verificación: un 5xx no dispara la alerta global
  // (`throwServerErrors`) y no se solapan peticiones. Nunca lanza: si falla,
  // la siguiente consulta lo reintenta.
  const isRefreshingRef = useRef(false);
  const refreshVerification = async (): Promise<void> => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try {
      await refreshProfile({ throwServerErrors: true });
    } catch {
      // Se reintenta en la siguiente consulta.
    } finally {
      isRefreshingRef.current = false;
    }
  };
  const refreshVerificationRef = useRef(refreshVerification);
  refreshVerificationRef.current = refreshVerification;

  // Espera de la activación. El webhook escribe `active` y `completed` en una
  // sola escritura, así que el perfil casi nunca muestra `completed` con el
  // panel pendiente: salta de `contract_sent` a activo. Por eso el fin del SDK
  // (`onComplete` u `onStatusChange('completed')`) ARRANCA la espera con este
  // flag. Es solo de la vista: vive en memoria, atado al panel, nunca se
  // guarda ni cuenta como estado del panel (no activa nada). Lo tiene el
  // proveedor para que sobreviva a cerrar el modal; se pierde al desmontar.
  const [awaitingActivationFor, setAwaitingActivationFor] = useState<
    string | null
  >(null);
  const [activationTimedOutFor, setActivationTimedOutFor] = useState<
    string | null
  >(null);

  // La espera no sobrevive a un cambio de panel (A → B → A), a que el panel
  // salga de `pending` ni a que el perfil PASE a `closed` (Legal cerró el
  // trámite después del SDK). Ajuste de estado al cambiar las props,
  // sin efecto (patrón de la documentación de React).
  const [prevWaitInputs, setPrevWaitInputs] = useState({
    corporateId,
    isPending,
    profileLegalStatus,
  });
  if (
    prevWaitInputs.corporateId !== corporateId ||
    prevWaitInputs.isPending !== isPending ||
    prevWaitInputs.profileLegalStatus !== profileLegalStatus
  ) {
    setPrevWaitInputs({ corporateId, isPending, profileLegalStatus });
    if (
      prevWaitInputs.corporateId !== corporateId ||
      !isPending ||
      profileLegalStatus === 'closed'
    ) {
      setAwaitingActivationFor(null);
      setActivationTimedOutFor(null);
    }
  }

  // El flag gana sobre un `closed` que el perfil ya traía: al abrir un trámite
  // nuevo desde `closed`, el perfil sigue diciendo `closed` hasta el primer
  // webhook del trámite nuevo. Lo que lo anula es la TRANSICIÓN a `closed`
  // (Legal cerró el trámite después del SDK), que limpia el flag arriba y
  // devuelve el modal al botón de trámite nuevo.
  const isProcessing =
    isPending &&
    (awaitingActivationFor === corporateId ||
      profileLegalStatus === 'completed');
  const isActivationDelayed = activationTimedOutFor === corporateId;
  const isPollingActivation = isProcessing && !isActivationDelayed;

  // Se consulta con el modal abierto o cerrado (el chip también dice
  // «Activando panel»). Para al activarse el panel, al desmontar o al pasar
  // `ACTIVATION_POLL_TIMEOUT_MS`; ahí el modal remite a soporte.
  useEffect(() => {
    if (!isPollingActivation) return undefined;
    const interval = window.setInterval(() => {
      void refreshVerificationRef.current();
    }, ACTIVATION_POLL_MS);
    const timeout = window.setTimeout(
      () => setActivationTimedOutFor(corporateId),
      ACTIVATION_POLL_TIMEOUT_MS,
    );
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [isPollingActivation, corporateId]);

  const startAwaitingActivation = (): void => {
    setAwaitingActivationFor(corporateId);
    setActivationTimedOutFor(null);
    void refreshVerification();
  };

  const retryActivationCheck = (): void => {
    setActivationTimedOutFor(null);
    void refreshVerification();
  };

  // Acción bloqueada por el backend (1185): el evento lo emite `ApiRest`.
  useEffect(() => {
    const onRequired = (event: Event): void => {
      const { canVerify: allowed } = (
        event as CustomEvent<PanelVerificationRequiredDetail>
      ).detail;
      if (allowed) setIsModalOpen(true);
      else setIsNoticeOpen(true);
    };
    window.addEventListener(PANEL_VERIFICATION_REQUIRED_EVENT, onRequired);
    return () =>
      window.removeEventListener(PANEL_VERIFICATION_REQUIRED_EVENT, onRequired);
  }, []);

  // Al entrar al panel (o cambiar a uno bloqueado), una vez por sesión. Con
  // `allow_operations` no se abre solo: el panel opera y la verificación queda
  // en el chip.
  useEffect(() => {
    if (!areOperationsBlocked || !canVerify || !corporateId) return;
    const key = `${AUTO_OPEN_KEY}:${corporateId}`;
    if (readFlag(window.sessionStorage, key)) return;
    writeFlag(window.sessionStorage, key);
    setIsModalOpen(true);
  }, [areOperationsBlocked, canVerify, corporateId]);

  // El tooltip se va solo; tocar el chip también lo cierra.
  useEffect(() => {
    if (!isCoachmarkVisible) return undefined;
    const timeout = window.setTimeout(
      () => setIsCoachmarkVisible(false),
      COACHMARK_DURATION_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [isCoachmarkVisible]);

  const closeModal = (): void => {
    setIsModalOpen(false);
    const key = `${COACHMARK_KEY}:${corporateId}`;
    if (isPending && !readFlag(window.localStorage, key)) {
      writeFlag(window.localStorage, key);
      setIsCoachmarkVisible(true);
    }
  };

  const openVerification = (): void => {
    setIsCoachmarkVisible(false);
    if (canVerify) setIsModalOpen(true);
    else setIsNoticeOpen(true);
  };

  const requireVerified = (action: () => void): void => {
    if (areOperationsBlocked) openVerification();
    else action();
  };

  // Último estado que reportó el SDK: cada estado nuevo se confirma con el
  // perfil (con la misma guarda), así un perfil atrasado —p. ej. `closed` con
  // un trámite nuevo en curso— se pone al día enseguida.
  const lastSdkStatusRef = useRef<string | null>(null);
  const handleSdkStatusChange = (status: string): void => {
    if (!isLegalVerificationStatus(status)) return;
    setLiveLegalStatus({
      corporateId,
      profileStatus: profileLegalStatus,
      status,
    });
    const isNewStatus = lastSdkStatusRef.current !== status;
    lastSdkStatusRef.current = status;
    // `completed` además arranca la espera de la activación.
    if (status === 'completed') startAwaitingActivation();
    else if (isNewStatus) void refreshVerification();
  };

  return (
    <PanelVerificationContext.Provider
      value={{
        isPending,
        areOperationsBlocked,
        canVerify,
        isCoachmarkVisible,
        // Durante la espera de la activación el chip dice «Activando panel»
        // (o que se demora, pasado el tope).
        legalStatus: isProcessing ? 'completed' : legalStatus,
        isActivationDelayed,
        openVerification,
        requireVerified,
      }}
    >
      {children}
      <PanelVerificationModal
        isOpen={isModalOpen}
        isActive={isActive}
        isProcessing={isProcessing}
        isActivationDelayed={isActivationDelayed}
        // Con la pista del SDK, como el chip: un perfil atrasado que sigue en
        // `closed` con el trámite nuevo ya en curso no vuelve a ofrecer otro.
        legalStatus={legalStatus}
        canVerify={canVerify}
        onClose={closeModal}
        onStatusChange={handleSdkStatusChange}
        onComplete={startAwaitingActivation}
        onRetryActivationCheck={retryActivationCheck}
        refreshProfile={refreshVerification}
      />
      <PanelVerificationNotice
        isOpen={isNoticeOpen}
        onClose={() => setIsNoticeOpen(false)}
      />
    </PanelVerificationContext.Provider>
  );
};
